import React, { useRef, useEffect } from 'react';

import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import { useNode, useEditor } from '@craftjs/core';

import SizeStyle from '@resolvers/tools/SizeStyle/SizeStyle';

import SettingsWrapper from '@common/components/SettingsWrapper/SettingsWrapper';
import SettingsRow from '@common/components/SettingsWrapper/SettingsRow';
import { EDITOR_RESOLVERS } from '@common/constants';
import {
  calcNodesWidth,
  isNodeEmailContainerColumn,
} from '@common/helpers/helpers';

const EmailContainerRow = ({ children, height }) => {
  const {
    connectors: { connect, drag },
    actions: { setCustom, setProp },
    childNodes,
  } = useNode((node) => ({
    childNodes: node.data.nodes,
  }));

  const { nodes, actions } = useEditor((state) => ({
    nodes: state.nodes,
  }));

  const ref = useRef(null);
  const { t } = useTranslation();

  const totalWidth = calcNodesWidth(childNodes, nodes);
  const hasOverflow = totalWidth > 100;

  useEffect(() => {
    if (ref?.current) {
      connect(drag(ref.current));
    }
  }, []);

  useEffect(() => {
    setTimeout(() => {
      setProp((props) => {
        props.isValid = !hasOverflow;
      });
    }, 0);
  }, [hasOverflow]);

  useEffect(() => {
    actions.history.ignore(() => {
      setTimeout(() => {
        setCustom((props) => {
          props.displayName = t('container_row');
        });
      }, 0);
    });
  }, [setCustom, t]);

  return (
    <>
      <tr
        ref={ref}
        style={{
          height: `${height}px`,
          fontSize: 'inherit',
          outline: hasOverflow ? '1px solid red' : 'none',
        }}
      >
        {children || <td style={{ minHeight: '10px' }}></td>}
      </tr>
      {hasOverflow && (
        <tr>
          <td colSpan={100} style={{ color: 'red', fontSize: '12px' }}>
            ⚠️ {`${t('total_width')}: ${totalWidth}%`}
          </td>
        </tr>
      )}
    </>
  );
};

const EmailContainerRowConfigPanel = () => {
  return (
    <SettingsWrapper>
      <SettingsRow sx={{ mb: 3 }}>
        <SizeStyle propKey='height' max={2000} label='height' />
      </SettingsRow>
    </SettingsWrapper>
  );
};

const EmailContainerRowProps = {
  height: '30',
  isValid: true,
  isDeletable: true,
};

EmailContainerRowProps.propTypes = {
  children: PropTypes.node,
  height: PropTypes.string,
  isValid: PropTypes.bool,
};

EmailContainerRow.craft = {
  displayName: EDITOR_RESOLVERS.EMAIL.CONTAINER_ROW,
  name: EDITOR_RESOLVERS.EMAIL.CONTAINER_ROW,
  props: EmailContainerRowProps,
  related: { settings: EmailContainerRowConfigPanel },
  rules: {
    canMoveIn: (incomingNodes) => {
      return incomingNodes.every((node) => {
        return isNodeEmailContainerColumn(node);
      });
    },
  },
};

export default EmailContainerRow;
